import React from 'react';
import * as ReactDOM from 'react-dom';
import styled from 'styled-components';
import { Grid as KendoGrid, GridColumn as Column } from '@progress/kendo-react-grid';
import { Button } from '@progress/kendo-react-buttons';
import { provideLocalizationService, registerForLocalization } from '@progress/kendo-react-intl';
import { useNavigate } from 'react-router-dom';
import NavLink from '../../../components/NavLink';
const NavigableRow = ({ rowLink, dataItem, row }) => {
  const navigate = useNavigate();
  const href = rowLink(dataItem);
  if (!href) { return row; }

  const handleClick = (e) => {
    if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.shiftKey) { return; }
    if (e.target.closest('button, a, input, .command-cell')) { return; }
    navigate(href);
  };

  return React.cloneElement(row, { onClick: handleClick, style: { cursor: 'pointer' } });
};

const ActionLink = styled.a`
  color: inherit;
  text-decoration: none;
  display: inline-flex;
  &:hover { color: inherit; text-decoration: none; }
`;

const NavigableAction = ({ href, children }) => {
  const navigate = useNavigate();
  const handleClick = (e) => {
    if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.shiftKey) { return; }
    e.preventDefault();
    e.stopPropagation();
    navigate(href);
  };
  return (
    <ActionLink href={href} onClick={handleClick}>
      {children}
    </ActionLink>
  );
};

const CommandButton = styled(Button).attrs({
  fillMode: 'solid'
})`
  margin-left: 4px;
  box-shadow: none;
  background-color: ${props => props.theme.gray15};
`;

const ButtonsCell = styled.td`
  overflow: visible !important;
`;

class Grid extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = { width: 0 };
    this.gridRef = React.createRef();
    this.updateWindowWidth = this.updateWindowWidth.bind(this);
    this.renderCommandCell = this.renderCommandCell.bind(this);
  }

  componentDidMount() {
    this.updateWindowWidth();
    window.addEventListener('resize', this.updateWindowWidth);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWindowWidth);
  }

  render() {
    const { isPageable = true, columns, children, actions, loading, rowLink, ...rest } = this.props;

    const baseRowRender = rest.rowRender;
    const rowRender = rowLink
      ? (row, rowProps) => {
        const rendered = baseRowRender ? baseRowRender(row, rowProps) : row;
        if (rowProps.rowType !== 'data') { return rendered; }
        return <NavigableRow rowLink={rowLink} dataItem={rowProps.dataItem} row={rendered} />;
      }
      : baseRowRender;

    if (rowLink) {
      delete rest.onRowClick;
    }

    return (
      <div>
        <div ref={this.gridRef}>
          <KendoGrid
            scrollable='none'
            sortable
            pageable={isPageable ? { pageSizes: false, buttonCount: 3 } : false}
            expandField='expanded'
            {...rest}
            rowRender={rowRender}
          >
            {children}
            {columns && columns.map((column, index) => {
              if (!column.minGridWidth || column.minGridWidth <= this.state.width) {
                if (rowLink && !column.skipNavLink) {
                  const originalCell = column.cell;
                  return <Column {...column} key={index} cell={(props) => {
                    let rendered;
                    if (originalCell) {
                      rendered = originalCell(props);
                    } else {
                      let value = props.dataItem[column.field];
                      if (value instanceof Date && !isNaN(value)) {
                        value = column.format === '{0:yyyy-MM-dd HH:mm}'
                          ? `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')} ${String(value.getHours()).padStart(2, '0')}:${String(value.getMinutes()).padStart(2, '0')}`
                          : `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
                      }
                      rendered = <td className={props.className}>{value}</td>;
                    }
                    if (props.rowType !== 'data') { return rendered; }
                    const href = rowLink(props.dataItem);
                    if (!href || !rendered?.props) { return rendered; }
                    const children = rendered.props.children;
                    const hasNavLink = children?.type === NavLink || children?.props?.to;
                    if (hasNavLink) { return rendered; }
                    const cls = rendered.props.className || '';
                    return React.cloneElement(rendered, { className: `${cls} link-cell` },
                      <NavLink to={href}><div className='link-cell-content'>{children}</div></NavLink>
                    );
                  }} />;
                }
                return <Column {...column} key={index} />;
              }
              return null;
            })}
            {actions && actions.length > 0 &&
              <Column
                headerClassName='command-cell'
                cell={this.renderCommandCell}
                width={`${actions.length * 58}px`}
                sortable={false}
              />
            }
          </KendoGrid>
        </div>
        {loading && <GridLoadingPanel/>}
      </div>
    );
  }

  renderCommandCell(e) {
    if (e.rowType === 'groupHeader') {
      return null;
    }
    const buttons = (
      <>
        {this.props.actions.map((action, index) =>{
          if (!action.renderOn || action.renderOn(e)) {
            const button = (
              <CommandButton
                key={index}
                icon={action.icon}
                onClick={action.href ? undefined : () => action.onClick(e)}
                disabled={action.disabledOn && action.disabledOn(e)}
                title={action.renderTitle && action.renderTitle(e)}
              >
                {action.label}
              </CommandButton>
            );
            if (action.href) {
              return <NavigableAction key={index} href={action.href(e)}>{button}</NavigableAction>;
            }
            return button;
          }
        })}
      </>
    );
    return (
      this.props.renderActionsCell?.(buttons, e) ||
      <ButtonsCell className='align-right command-cell'>
        {buttons}
      </ButtonsCell>
    );
  }

  updateWindowWidth() {
    this.setState({ width: this.gridRef.current.offsetWidth });
  }
}

class GridLoadingPanel extends React.Component {
  render() {
    const localizationService = provideLocalizationService(this);
    const loadingPanel = (
      <div className='k-loading-mask'>
        <span className='k-loading-text'>{localizationService.toLanguageString('custom.loading')}</span>
        <div className='k-loading-image' />
        <div className='k-loading-color' />
      </div>
    );
    const gridContent = document && document.querySelector('.k-grid');
    return gridContent
      ? ReactDOM.createPortal(loadingPanel, gridContent)
      : loadingPanel;
  }
}

registerForLocalization(GridLoadingPanel);

export default Grid;
